'use client';
import { useState, ReactElement, useCallback, ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { ScrollArea } from '@components/ui/scroll-area';
import { motion, AnimatePresence } from 'framer-motion';

type StepComponentProps = {
  onGoNext: (nextStepId?: string) => void;
  onGoBack: (stepId?: string) => void;
};

type Step = {
  id: string;
  title: string;
  component: ReactElement | ((props: StepComponentProps) => ReactElement);
};

type MultiLevelDialogProps = {
  steps: Step[];
  isOpen: boolean;
  onClose: () => void;
  footer?:
    | ReactNode
    | ((props: { onGoNext: StepComponentProps['onGoNext']; onGoBack: StepComponentProps['onGoBack'] }) => ReactNode);
  currentStepId: string;
  onStepChange: (stepId: string) => void;
};

export default function MultiLevelDialog({
  steps,
  isOpen,
  onClose,
  footer,
  currentStepId,
  onStepChange
}: MultiLevelDialogProps) {
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [stepHistory, setStepHistory] = useState<string[]>(['main']);

  const currentStep = steps.find(step => step.id === currentStepId);

  const handleGoNext = useCallback(
    (nextStepId?: string) => {
      if (nextStepId) {
        setDirection('forward');
        setStepHistory(prev => [...prev, nextStepId]);
        onStepChange(nextStepId);
      }
    },
    [onStepChange]
  );

  const handleGoBack = useCallback(
    (stepId?: string) => {
      if (stepHistory.length > 1) {
        setDirection('backward');
        let newHistory;
        if (stepId) {
          newHistory = stepHistory.slice(0, stepHistory.indexOf(stepId) + 1);
        } else {
          newHistory = stepHistory.slice(0, -1);
        }
        const previousStepId = newHistory[newHistory.length - 1];
        setStepHistory(newHistory);
        if (previousStepId) {
          onStepChange(previousStepId);
        }
      }
    },
    [stepHistory, onStepChange]
  );

  const variants = {
    enter: (direction: 'forward' | 'backward') => ({
      x: direction === 'forward' ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: 'forward' | 'backward') => ({
      x: direction === 'forward' ? '-100%' : '100%',
      opacity: 0
    })
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex flex-col h-[100svh] md:h-3/4 max-w-screen-sm p-0 md:p-6 gap-0 overflow-hidden">
        <DialogHeader className="bg-red-500/0 flex flex-row items-center justify-center py-2 relative">
          <DialogTitle className="">{currentStep?.title}</DialogTitle>
          {stepHistory.length > 1 && (
            <ChevronLeft className="h-6 w-6 cursor-pointer absolute left-0 top-0" onClick={() => handleGoBack()} />
          )}
        </DialogHeader>
        <div className="flex-grow relative">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={currentStep?.id}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute inset-0 h-full"
            >
              <ScrollArea className="h-full w-full p-4 ">
                <div className="flex flex-col gap-6 h-full">
                  {typeof currentStep?.component === 'function'
                    ? currentStep?.component({ onGoNext: handleGoNext, onGoBack: handleGoBack })
                    : currentStep?.component}
                </div>
              </ScrollArea>
            </motion.div>
          </AnimatePresence>
        </div>
        {footer && (
          <DialogFooter className="p-4 gap-2">
            {typeof footer === 'function' ? footer({ onGoNext: handleGoNext, onGoBack: handleGoBack }) : footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
